import { TextFieldPropertiesType } from "@website/ui/text-field/text-field.types";
import { numberConvert } from "@website/utils/number-convert";
import { numberSeparator } from "@website/utils/number-separator";
import pick from "lodash-es/pick";
import {
  ChangeEvent,
  ChangeEventHandler,
  FocusEvent,
  MouseEvent,
  useCallback,
  useRef
} from "react";

export const useTextField = (properties: TextFieldPropertiesType) => {
  const {
    onChange,
    onClick,
    type,
    inputMode,
    hasSeparator,
    onFocus,
    onBlur,
    onStartIconClick,
    onEndIconClick
  } = properties;
  const numberOnly = ["number", "tel"].includes(type ?? "text");

  const inputReference = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  const onRootClick = useCallback(() => {
    inputReference.current?.focus();
    onClick?.();
  }, [onClick]);

  const onInputFocus = useCallback(
    (event: FocusEvent<HTMLInputElement> & FocusEvent<HTMLTextAreaElement>) => {
      if (onFocus) {
        onFocus(event);
      }
      // setHasFocus(true);
    },
    [onFocus]
  );

  const onInputBlur = useCallback(
    (event: FocusEvent<HTMLInputElement> & FocusEvent<HTMLTextAreaElement>) => {
      if (onBlur) {
        onBlur(event);
      }
      // setHasFocus(false);
    },
    [onBlur]
  );

  const onStartIconClickHandler = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (onStartIconClick) {
        event.stopPropagation();
        onStartIconClick(event);
      }
    },
    [onStartIconClick]
  );

  const onEndIconClickHandler = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (onEndIconClick) {
        event.stopPropagation();
        onEndIconClick(event);
      }
    },
    [onEndIconClick]
  );

  const handleOnChange: ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = useCallback(
    (event) => {
      if (type !== "file")
        event.target.value = numberConvert(
          hasSeparator
            ? numberSeparator(event.target.value)
            : event.target.value,
          { locale: "en", numberOnly }
        );
      onChange?.(
        event as ChangeEvent<HTMLInputElement> &
          ChangeEvent<HTMLTextAreaElement>
      );
    },
    [hasSeparator, numberOnly, onChange, type]
  );

  return {
    rootProperties: {
      onClick: onRootClick
      // ...pick(props, "aria-label", "onClick", "type"),
      // disabled: props.disabled || props.loading,
    },
    inputProperties: {
      ...pick(properties, [
        "disabled",
        "placeholder",
        "value",
        "defaultValue",
        "min",
        "max",
        "type",
        "readOnly",
        "data-testid",
        "name",
        "maxLength",
        "autoComplete",
        "autoFocus"
      ]),
      ref: inputReference,
      onChange: handleOnChange,
      inputMode: numberOnly ? ("numeric" as const) : inputMode,
      onFocus: onInputFocus,
      onBlur: onInputBlur
    },
    startIconProperties: {
      onClick: onStartIconClickHandler
    },
    endIconProperties: {
      onClick: onEndIconClickHandler
    }
  };
};
